import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * `React.lazy` that survives a deploy.
 *
 * Since the route-level split (#35) a navigation fetches a chunk by its hashed
 * filename. When a deploy replaces the build, those filenames are gone, so a
 * session that was open across the deploy gets a 404 for a chunk that no longer
 * exists. Plain `lazy()` rejects, and the only boundary above it is the
 * app-level one in `main.tsx` — so a user who merely clicked a nav link loses
 * the whole app to an error screen.
 *
 * The recovery is the standard one: the HTML is not hashed, so reloading picks
 * up a fresh index.html that names the new chunks.
 *
 * Deliberate choices:
 *  - Only a chunk-load failure triggers it. A real error *inside* the module
 *    must still reach the error boundary, or a reload loop would hide a bug.
 *  - One retry first, for a transient network blip. The URL is unchanged, so a
 *    genuine 404 fails again immediately and costs nothing.
 *  - At most one reload per RELOAD_WINDOW_MS. A permanently missing chunk (a
 *    half-uploaded deploy) must not put the browser in a reload loop, and a
 *    timestamp re-arms it for a later, unrelated deploy — which a one-shot flag
 *    cleared on success would not do safely.
 */
const RELOAD_KEY = 'app.chunk-reload-at';
const RELOAD_WINDOW_MS = 10 * 60 * 1000;

/** Message shapes the bundlers and browsers use for "the chunk is not there". */
const CHUNK_ERROR =
  /ChunkLoadError|Loading chunk|Loading CSS chunk|error loading dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed/i;

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) return CHUNK_ERROR.test(`${error.name}: ${error.message}`);
  return CHUNK_ERROR.test(String(error));
}

/** Storage can throw (private mode, blocked cookies); never let that break a route. */
function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Best-effort. Without storage we may reload twice; the error boundary
    // still ends it, and that is better than never recovering.
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React's own
// LazyExoticComponent is written against ComponentType<any>; narrowing it here
// makes every call site fail on its props.
export function lazyChunk<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      try {
        return await factory();
      } catch {
        // still gone — the build really was replaced
      }
      if (reloadedRecently()) throw error;
      markReloaded();
      window.location.reload();
      // Keep the Suspense fallback up while the page goes away: resolving or
      // rejecting here would flash an error the user never needs to see.
      return new Promise<never>(() => {});
    }
  });
}
